/**
 * formStoreUrlListener
 *
 * Single non-React owner of `formStore.state.formId` / `versionId`. Subscribes
 * to the data router and mirrors the URL into the store with one mutation per
 * real change. Replaces the render-body URL writers that used to ping-pong
 * during route transitions and storm every `useSnapshot` subscriber in the
 * form-detail tree.
 *
 * Sync is gated on Firebase auth: writing `formId` triggers
 * `formStore.subscribe` to attach Firestore listeners on `forms/${formId}`,
 * which would be rejected if the user is not signed in. We start syncing once
 * `onAuthStateChanged` reports a user and stop on sign-out.
 *
 * `installFormStoreUrlListener(router)` must be called once, from the same
 * module that creates the data router, so we avoid an import cycle (the form
 * store is imported from many places, including children of the router itself).
 */

import { subscribe } from 'valtio';
import type {
    NavigateFunction,
    NavigateOptions,
    RouterNavigateOptions,
    To,
} from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../services/startAuth';
import { formStore } from './formStore';

type RouterPathState = { location: { pathname: string } };

type Router = {
    state: RouterPathState;
    subscribe: (callback: (state: RouterPathState) => void) => () => void;
    navigate: {
        (to: number): unknown;
        (to: To | null, options?: RouterNavigateOptions): unknown;
    };
};

type FormRouteIds = {
    formId: string | undefined;
    versionId: string | undefined;
};

const FORM_DETAIL_PATTERN = /^\/forms\/([^/]+)(?:\/formVersions\/([^/]+))?\/?$/;

const parseFormRoute = (pathname: string): FormRouteIds => {
    const match = pathname.match(FORM_DETAIL_PATTERN);
    return {
        formId: match?.[1],
        versionId: match?.[2],
    };
};

let installedRouter: Router | null = null;
let routerUnsubscribe: (() => void) | null = null;
let activateCleanup: (() => void) | null = null;

const syncFromPathname = (pathname: string): void => {
    const { formId, versionId } = parseFormRoute(pathname);

    if (formStore.state.formId !== formId) {
        formStore.state.formId = formId;
    }
    if (formStore.state.versionId !== versionId) {
        formStore.state.versionId = versionId;
    }

    const onFormDetailRoute = formId !== undefined;
    if (onFormDetailRoute && !activateCleanup) {
        activateCleanup = formStore.activate();
    } else if (!onFormDetailRoute && activateCleanup) {
        activateCleanup();
        activateCleanup = null;
    }
};

const startSyncing = (): void => {
    if (!installedRouter || routerUnsubscribe) return;
    routerUnsubscribe = installedRouter.subscribe((state) => {
        syncFromPathname(state.location.pathname);
    });
    syncFromPathname(window.location.pathname);
};

const stopSyncing = (): void => {
    routerUnsubscribe?.();
    routerUnsubscribe = null;
    if (activateCleanup) {
        activateCleanup();
        activateCleanup = null;
    }
};

const buildNavigateFn = (router: Router): NavigateFunction =>
    ((toOrDelta: To | number, options?: NavigateOptions) => {
        if (typeof toOrDelta === 'number') {
            return router.navigate(toOrDelta);
        }
        return router.navigate(toOrDelta, options);
    }) as NavigateFunction;

export const installFormStoreUrlListener = (router: Router): void => {
    if (installedRouter) return;
    installedRouter = router;

    formStore.navigate = buildNavigateFn(router);

    onAuthStateChanged(auth, (user) => {
        if (user) {
            startSyncing();
        } else {
            stopSyncing();
        }
    });

    subscribe(formStore.state, () => {
        const formId = formStore.state.formId;
        const versionId = formStore.state.versionId;
        const currentVersionId = formStore.state.form.data?.currentVersionId;
        if (!formId || versionId || !currentVersionId) return;

        const target = `/forms/${formId}/formVersions/${currentVersionId}`;
        if (window.location.pathname === target) return;

        formStore.navigate?.(target);
    });
};
